/**
 * EVO — asosiy karkas: yon menyu + yuqori panel + kontent.
 *
 * Kontent maydoni alohida skroll qiladi (`overflow-auto`), sahifa emas —
 * shunda jadval sarlavhasi va yuqori paneldagi kurs doim ko'rinib turadi.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useT } from '../../lib/i18n-context';
import { PageTitleProvider } from './page-title';
import { PartnerViewBanner } from './partner-view';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

export function AppShell(): ReactNode {
  const t = useT();

  /*
   * Telefondagi menyu tortmasi.
   *
   * Holat SHU YERDA turadi, `Sidebar` ichida emas: uni yuqori paneldagi
   * tugma ochadi, tortmaning o'zi va Escape yopadi — ya'ni uchta joy bir
   * qiymatga tegadi. Bolaning ichida saqlansa, ota-onaga ko'tarish uchun
   * baribir shu yerga chiqarish kerak bo'lardi.
   */
  const [navOpen, setNavOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /*
   * Marshrut o'zgarganda tortma YOPILADI.
   *
   * Bandni bosgan odam kontentni ko'rmoqchi. Tortma ochiq qolsa, u ekranning
   * uchdan ikkisini yopib turadi va foydalanuvchi uni QO'LDA yopishi kerak
   * bo'lardi — har safar, har o'tishda.
   */
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  /*
   * Escape — klaviatura bilan yopish.
   *
   * Tortma ochiq bo'lmasa tinglovchi umuman ulanmaydi: har bosishda
   * ishlaydigan global tinglovchi qolgan ekranlarga sababsiz yuk beradi.
   */
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    /*
     * Sarlavha provideri ENG TASHQARIDA: uni ham yuqori panel (o'qiydi),
     * ham kontent (yozadi) ichida bo'lishi kerak. Faqat `<main>` ni o'rasak,
     * yuqori panel qiymatni ko'rmasdi.
     */
    <PageTitleProvider>
    {/*
      `evo-print-flow` — chop etishda balandlik va aylanish chegaralarini
      bo'shatadi. `h-screen` + `overflow-hidden` ekranda to'g'ri (ish maydoni
      `<main>` ichida aylanadi), qog'ozda esa halokatli: brauzer faqat
      ko'rinib turgan qismni oladi va qolganini KESADI. Sabab `styles.css`
      dagi `@media print` ramkasida.
    */}
    <div className="evo-print-flow flex h-screen overflow-hidden bg-canvas">
      {/* Yon menyu qog'ozga tushmaydi — u boshqaruv, ma'lumot emas. */}
      <div className="evo-no-print contents">
        <Sidebar mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      </div>

      <div className="evo-print-flow flex min-w-0 flex-1 flex-col">
        {/*
          PARTNER TASHKILOT BANNERI — yuqori panelning USTIDA va karkasning
          ichida, ya'ni HAR ekranda ko'rinadi va kontent bilan birga
          aylanmaydi. O'z tashkilotimizda hech narsa chizmaydi (`null`),
          shuning uchun odatdagi ish uchun bu qator bepul.

          Nima uchun `evo-no-print` YO'Q — `partner-view.tsx` dagi ramkada:
          partner ekranidan chiqarilgan qog'oz kimning ma'lumoti ekanini
          o'zi aytishi kerak.
        */}
        <PartnerViewBanner />

        {/* Klaviatura bilan ishlaydigan foydalanuvchi 30 ta menyu bandidan
            sakrab o'tishi kerak (GAP_ANALYSIS §C3 — sichqonchasiz ish). */}
        <a
          href="#evo-main"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-brand focus:px-3 focus:py-2 focus:text-brand-ink"
        >
          {t('nav.skipToContent')}
        </a>

        {/* Yuqori panel ham boshqaruv — qog'ozda o'rniga hisobot sarlavhasi
            chiziladi (`report-shell.tsx` dagi `evo-print-only` bloki). */}
        <div className="evo-no-print contents">
          <Topbar onMenuClick={() => setNavOpen(true)} />
        </div>

        {/*
           * Yon bo'shliq tepa/pastdan KATTAROQ: chapda yon menyu chegarasi,
           * o'ngda ekran cheti — ular o'zi ajratib turadi. Tepada esa
           * yuqori panel bilan kontent orasida nafas kerak, aks holda
           * sarlavha jadval sarlavhasiga yopishib qoladi.
           */}
        <main
          id="evo-main"
          className="evo-print-flow min-w-0 flex-1 overflow-auto px-4 py-4 sm:px-6 sm:py-5"
        >
          <Outlet />
        </main>
      </div>
    </div>
    </PageTitleProvider>
  );
}
